import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { PublicProduct, PublicVariant, PublicModifierGroup } from '../../../shared/api/public-types.js'
import { addCartItem } from '../../../shared/api/cart.js'
import { showToast } from '../../../shared/ui/Toast.js'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css/bundle'

interface Props {
  product: PublicProduct
  currency: string
  onClose: () => void
  orgSlug: string
  locSlug: string
  onAddedToCart?: () => void
  orderingBlocked?: boolean
  blockedMessage?: string | null
}

export default function ProductDetail({
  product, currency, onClose, orgSlug, locSlug, onAddedToCart,
  orderingBlocked = false, blockedMessage = null,
}: Props) {
  const { t } = useTranslation()
  const [selectedVariant, setSelectedVariant] = useState<PublicVariant | null>(product.baseVariant ? {
    id: product.baseVariant.id,
    name: product.baseVariant.name,
    price: product.baseVariant.price,
    oldPrice: product.baseVariant.oldPrice,
    isDefault: true,
    sortOrder: 0,
  } : (product.variants[0] ?? null))
  const [quantities, setQuantities] = useState<Record<string, Record<string, number>>>({})

  const allImages = product.images?.length > 0 ? product.images : (product.imageUrl ? [product.imageUrl] : [])

  const price = selectedVariant?.price ?? product.baseVariant?.price ?? 0
  const oldPrice = selectedVariant?.oldPrice ?? product.baseVariant?.oldPrice

  function getModQty(groupId: string, modId: string): number {
    return quantities[groupId]?.[modId] ?? 0
  }

  function setModQty(groupId: string, modId: string, val: number) {
    setQuantities(prev => ({
      ...prev,
      [groupId]: { ...(prev[groupId] ?? {}), [modId]: val },
    }))
  }

  function selectedCount(group: PublicModifierGroup): number {
    return Object.values(quantities[group.id] ?? {}).filter(v => v > 0).length
  }

  const hasVariants = product.variants.length > 1
  const hasModifiers = product.modifierGroups.length > 0

  const [submitting, setSubmitting] = useState(false)
  const [addError, setAddError] = useState<string | null>(null)

  async function handleAddToCart() {
    if (orderingBlocked) return
    setSubmitting(true)
    setAddError(null)
    try {
      const selectedMods = []
      for (const group of product.modifierGroups) {
        const gQtys = quantities[group.id] ?? {}
        for (const mod of group.modifiers) {
          const qty = gQtys[mod.id] ?? 0
          if (qty > 0) {
            selectedMods.push({ modifierGroupId: group.id, modifierId: mod.id, quantity: qty })
          }
        }
      }
      await addCartItem(orgSlug, locSlug, {
        productId: product.id,
        variantId: selectedVariant?.id,
        quantity: 1,
        modifiers: selectedMods.length > 0 ? selectedMods : undefined,
      })
      showToast(t('storefront.addedToCart', { name: product.name }))
      onAddedToCart?.()
      onClose()
    } catch (err: unknown) {
      setAddError(err instanceof Error ? err.message : 'Failed to add item')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="storefront-detail-overlay" onClick={onClose}>
      <div className="storefront-detail-modal" onClick={e => e.stopPropagation()}>
        <button className="storefront-detail-close" onClick={onClose}>&times;</button>

        {allImages.length > 1 && (
          <Swiper
            className="storefront-detail-gallery"
            slidesPerView={1}
            grabCursor
            pagination={{ clickable: true }}
            keyboard
            watchSlidesProgress
          >
            {allImages.map((src, i) => (
              <SwiperSlide key={i}>
                <img
                  src={src}
                  alt={product.name}
                  className="storefront-detail-img"
                  loading="lazy"
                />
              </SwiperSlide>
            ))}
          </Swiper>
        )}
        {allImages.length === 1 && (
          <div className="storefront-detail-gallery">
            <img
              src={allImages[0]!}
              alt={product.name}
              className="storefront-detail-img"
              loading="lazy"
            />
          </div>
        )}
        {allImages.length === 0 && (
          <div className="storefront-detail-no-img">{t('storefront.noImage')}</div>
        )}

        <h2 className="storefront-detail-name">{product.name}</h2>

        {product.spiceLevel > 0 && (
          <p className="storefront-detail-spice">{'🌶'.repeat(product.spiceLevel)}</p>
        )}

        {product.description && <p className="storefront-detail-desc">{product.description}</p>}
        {product.composition && <p className="storefront-detail-comp">{t('storefront.composition')}: {product.composition}</p>}

        {(product.weight || product.calories) && (
          <div className="storefront-detail-nutrition">
            {product.weight && <span>{product.weight} {t('storefront.grams')}</span>}
            {product.volume && <span>{product.volume} {t('storefront.milliliters')}</span>}
            {product.calories && <span>{product.calories} {t('storefront.kcal')}</span>}
          </div>
        )}

        {hasVariants && (
          <div className="storefront-detail-section">
            <h3>{t('storefront.variants')}</h3>
            <div className="storefront-variant-list">
              {product.variants.map(v => (
                <label key={v.id} className={`storefront-variant-btn ${selectedVariant?.id === v.id ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="variant"
                    checked={selectedVariant?.id === v.id}
                    onChange={() => setSelectedVariant(v)}
                  />
                  <span className="storefront-variant-name">{v.name}</span>
                  <span className="storefront-variant-price">{v.price} ₽</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {price > 0 && (
          <div className="storefront-detail-price">
            {oldPrice != null && oldPrice > 0 && (
              <span className="storefront-old-price">{oldPrice} ₽</span>
            )}
            <span className="storefront-current-price">{price} ₽</span>
          </div>
        )}

        {hasModifiers && (
          <div className="storefront-detail-section">
            <h3>{t('storefront.additions')}</h3>
            {product.modifierGroups.map(group => (
              <div key={group.id} className="storefront-modifier-group">
                <p className="storefront-modifier-group-name">
                  {group.name}
                  {group.isRequired && <span className="storefront-required">*</span>}
                  {group.max != null && <span className="storefront-modifier-limit">({t('storefront.selectUpTo')} {group.max})</span>}
                </p>
                <div className="storefront-modifier-list">
                  {group.modifiers.map(mod => {
                    const qty = getModQty(group.id, mod.id)
                    const canAdd = group.max == null || selectedCount(group) < group.max
                    return (
                      <div key={mod.id} className="storefront-modifier-row">
                        <span className="storefront-modifier-name">
                          {mod.name} <span className="storefront-modifier-price">+{mod.price} {currency}</span>
                        </span>
                        <div className="storefront-qty-control">
                          <button
                            type="button"
                            disabled={qty <= 0}
                            onClick={() => setModQty(group.id, mod.id, qty - 1)}
                          >&minus;</button>
                          <span>{qty}</span>
                          <button
                            type="button"
                            disabled={!canAdd || qty >= mod.maxQuantity}
                            onClick={() => setModQty(group.id, mod.id, qty + 1)}
                          >+</button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {addError && <p className="storefront-detail-error">{addError}</p>}

        <span
          className="storefront-add-to-cart-wrap"
          title={orderingBlocked ? blockedMessage ?? undefined : undefined}
        >
          <button
            type="button"
            className="storefront-add-to-cart"
            disabled={submitting || orderingBlocked}
            aria-disabled={submitting || orderingBlocked}
            title={orderingBlocked ? blockedMessage ?? undefined : undefined}
            onClick={handleAddToCart}
          >
            {submitting ? t('storefront.adding') : t('storefront.addToCart')}
          </button>
        </span>
      </div>
    </div>
  )
}